
import React, { useState, useEffect } from 'react';
import { adminSettingsService, PlatformConfig } from '../../services/admin.settings.mock';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { 
  ShieldAlert, 
  Settings as SettingsIcon, 
  Zap, 
  Lock, 
  Globe, 
  DollarSign, 
  RefreshCcw, 
  CheckCircle2, 
  AlertTriangle,
  Cpu,
  Mail,
  ShieldCheck,
  ChevronRight
} from 'lucide-react';

const AdminSettings: React.FC = () => {
  const [config, setConfig] = useState<PlatformConfig | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    setConfig(adminSettingsService.getConfig());
  }, []);

  const handleUpdate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!config) return;

    setIsSaving(true);
    const success = await adminSettingsService.updateConfig(config);
    if (success) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }
    setIsSaving(false);
  };

  const toggleConfig = (key: keyof PlatformConfig) => {
    if (!config) return;
    setConfig({ ...config, [key]: !config[key] });
  };

  if (!config) return null;

  return (
    <div className="max-w-6xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-8 duration-1000 pb-32">
      
      {/* 1. Header with Global Status */}
      <section className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-1">
          <h2 className="text-brand-muted text-[11px] font-black uppercase tracking-[0.6em] flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-brand-primary animate-pulse shadow-[0_0_10px_#FFCE1B]"></span>
            Kernel Configuration
          </h2>
          <h1 className="text-4xl font-black text-white tracking-tighter">Platform Architecture</h1>
        </div>

        <div className="flex items-center gap-4">
          {showSuccess && (
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl animate-in zoom-in duration-300">
              <CheckCircle2 size={16} className="text-emerald-500" />
              <span className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">Protocol Synced</span>
            </div>
          )}
          <Button onClick={handleUpdate} isLoading={isSaving} className="h-14 px-10 shadow-xl shadow-brand-primary/10">
            SAVE GLOBAL CHANGES
          </Button>
        </div>
      </section>

      {/* 2. Critical System Overrides */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className={`glass-card p-10 rounded-[3rem] border transition-all duration-500 ${config.maintenanceMode ? 'border-red-500/40 bg-red-500/5' : 'border-white/5'}`}>
          <div className="flex items-center justify-between mb-8">
             <div className="space-y-1">
                <h3 className="text-lg font-black text-white uppercase tracking-tight">Maintenance Protocol</h3>
                <p className="text-xs text-brand-muted">Deactivate terminal access for all users</p>
             </div>
             <ShieldAlert className={config.maintenanceMode ? 'text-red-500 animate-pulse' : 'text-brand-muted'} size={32} />
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-[10px] font-black uppercase tracking-widest ${config.maintenanceMode ? 'text-red-500' : 'text-emerald-500'}`}>
              System {config.maintenanceMode ? 'OFFLINE' : 'OPERATIONAL'}
            </span>
            <button 
              onClick={() => toggleConfig('maintenanceMode')}
              className={`w-14 h-8 rounded-full relative transition-all duration-500 ${config.maintenanceMode ? 'bg-red-500' : 'bg-white/10'}`}
            >
              <div className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-all duration-500 ${config.maintenanceMode ? 'left-7 shadow-lg' : 'left-1'}`}></div>
            </button>
          </div>
        </div>

        <div className={`glass-card p-10 rounded-[3rem] border transition-all duration-500 ${!config.tradingEnabled ? 'border-amber-500/40 bg-amber-500/5' : 'border-white/5'}`}>
          <div className="flex items-center justify-between mb-8">
             <div className="space-y-1">
                <h3 className="text-lg font-black text-white uppercase tracking-tight">Trading Engine</h3>
                <p className="text-xs text-brand-muted">Global switch for market execution</p>
             </div>
             <Zap className={config.tradingEnabled ? 'text-brand-primary animate-pulse' : 'text-brand-muted'} size={32} />
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-[10px] font-black uppercase tracking-widest ${config.tradingEnabled ? 'text-emerald-500' : 'text-amber-500'}`}>
              Execution {config.tradingEnabled ? 'ACTIVE' : 'HALTED'}
            </span>
            <button 
              onClick={() => toggleConfig('tradingEnabled')}
              className={`w-14 h-8 rounded-full relative transition-all duration-500 ${config.tradingEnabled ? 'bg-brand-primary' : 'bg-white/10'}`}
            >
              <div className={`absolute top-1 w-6 h-6 rounded-full bg-black transition-all duration-500 ${config.tradingEnabled ? 'left-7' : 'left-1'}`}></div>
            </button>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        
        {/* 3. Financial Thresholds */}
        <div className="lg:col-span-7 space-y-10">
          <section className="glass-card rounded-[3.5rem] p-10 space-y-10">
            <h3 className="text-sm font-black text-white uppercase tracking-[0.3em] flex items-center gap-3">
              <DollarSign size={18} className="text-brand-primary" />
              Treasury Parameters
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Input 
                label="Min Withdrawal (USD)" 
                type="number"
                value={config.minWithdrawal}
                onChange={(e) => setConfig({...config, minWithdrawal: parseFloat(e.target.value)})}
                className="font-mono"
              />
              <Input 
                label="Max Daily Liquidity (USD)" 
                type="number"
                value={config.maxWithdrawal}
                onChange={(e) => setConfig({...config, maxWithdrawal: parseFloat(e.target.value)})}
                className="font-mono"
              />
              <Input 
                label="Market Execution Fee (%)" 
                type="number"
                step="0.1"
                value={config.tradingFeePercent}
                onChange={(e) => setConfig({...config, tradingFeePercent: parseFloat(e.target.value)})}
                className="font-mono"
              />
              <Input 
                label="Referral Yield (%)" 
                type="number"
                step="0.1"
                value={config.referralBonusPercent}
                onChange={(e) => setConfig({...config, referralBonusPercent: parseFloat(e.target.value)})}
                className="font-mono"
              />
            </div>
          </section>

          <section className="glass-card rounded-[3.5rem] p-10 space-y-10">
            <h3 className="text-sm font-black text-white uppercase tracking-[0.3em] flex items-center gap-3">
              <Lock size={18} className="text-brand-primary" />
              Security & Compliance
            </h3>

            <div className="space-y-6">
              <ToggleRow 
                label="Enforce Global KYC" 
                desc="Requires identity verification for any capital redemption"
                active={config.requireKycForWithdrawal}
                onToggle={() => toggleConfig('requireKycForWithdrawal')}
              />
              <ToggleRow 
                label="Admin Hardware Key" 
                desc="Enforce 2FA/Hardware tokens for all administrative sessions"
                active={config.enforce2FAForAdmins}
                onToggle={() => toggleConfig('enforce2FAForAdmins')}
              />
            </div>
          </section>
        </div>

        {/* 4. Support & Node Info */}
        <div className="lg:col-span-5 space-y-10">
          <section className="glass-card rounded-[3.5rem] p-10 space-y-8">
            <h3 className="text-sm font-black text-white uppercase tracking-[0.3em] flex items-center gap-3">
              <Globe size={18} className="text-brand-primary" />
              Platform Metadata
            </h3>
            
            <div className="space-y-6">
              <Input 
                label="Global Node Identifier"
                value={config.systemNodeId}
                onChange={(e) => setConfig({...config, systemNodeId: e.target.value})}
                className="font-mono uppercase"
              />
              <Input 
                label="Compliance Support Hub"
                type="email"
                value={config.supportEmail}
                onChange={(e) => setConfig({...config, supportEmail: e.target.value})}
              />
            </div>

            <div className="pt-6 border-t border-white/5 space-y-4">
              <div className="flex items-center justify-between text-[11px] font-black uppercase tracking-widest text-brand-muted">
                <span>System Integrity</span>
                <span className="text-emerald-500">OPTIMAL</span>
              </div>
              <div className="flex gap-1.5 h-1">
                 {[1,2,3,4,5,6,7,8,9,10].map(i => (
                   <div key={i} className={`flex-1 rounded-full ${i < 9 ? 'bg-brand-primary' : 'bg-white/10'}`}></div>
                 ))}
              </div>
            </div>
          </section>

          {/* Hardcode Info Banner */}
          <div className="bg-brand-primary/5 border border-brand-primary/20 rounded-[2.5rem] p-10 space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-brand-primary/20 flex items-center justify-center text-brand-primary border border-brand-primary/30 mb-2">
               <Cpu size={24} />
            </div>
            <h4 className="text-white font-black uppercase tracking-tight">Nexus Kernel v4.2</h4>
            <p className="text-[11px] text-brand-muted leading-relaxed uppercase font-black tracking-widest opacity-80">
              Operational updates are pushed to the decentralized ledger every 60 seconds. High-stakes overrides require a secondary Nexus key.
            </p>
            <button className="w-full mt-4 group flex items-center justify-between px-6 py-4 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 transition-all active:scale-[0.98]">
               <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white">Kernel Logs</span>
               <ChevronRight size={16} className="text-brand-primary group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ToggleRow: React.FC<{ label: string, desc: string, active: boolean, onToggle: () => void }> = ({ label, desc, active, onToggle }) => (
  <div className="flex items-center justify-between p-6 bg-white/5 rounded-3xl border border-white/5 hover:border-white/10 transition-all cursor-pointer group" onClick={onToggle}>
    <div className="space-y-1">
      <h4 className="text-[11px] font-black text-white uppercase tracking-widest">{label}</h4>
      <p className="text-[10px] text-brand-muted font-bold tracking-tight opacity-70">{desc}</p>
    </div>
    <button className={`w-12 h-6 rounded-full relative transition-all duration-500 ${active ? 'bg-brand-primary' : 'bg-white/10'}`}>
      <div className={`absolute top-1 w-4 h-4 rounded-full transition-all duration-500 ${active ? 'left-7 bg-black' : 'left-1 bg-white'}`}></div>
    </button>
  </div>
);

export default AdminSettings;
